import { useState, useEffect } from 'react'
import AuthService from '../services/auth'
import { UserContext } from './UserContext'

export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [authChecked, setAuthChecked] = useState(false)
  const getInitialTheme = () => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'dark'
  }

  const [theme, setTheme] = useState(getInitialTheme)

  useEffect(() => {
    // Don't redirect if we're on public pages
    const currentPath = window.location.pathname
    const isPublicPage = currentPath === '/login' || currentPath === '/onboarding'

    if (isPublicPage) {
      // Skip auth check entirely on public pages to avoid 401 errors
      setAuthChecked(true)
      return
    }

    AuthService.checkAuth()
      .then((authStatus) => {
        if (!authStatus.isAuthenticated) {
          AuthService.redirectToLogin()
        } else {
          setCurrentUser(authStatus.user)
        }
        setAuthChecked(true)
      })
      .catch(() => {
        AuthService.redirectToLogin()
        setAuthChecked(true)
      })
  }, [])

  // Load user-specific theme when user changes
  useEffect(() => {
    if (currentUser?.id) {
      const userThemeKey = `user_${currentUser.id}_theme`
      const savedTheme = localStorage.getItem(userThemeKey)
      if (savedTheme) {
        setTheme(savedTheme)
      }
    }
  }, [currentUser?.id])

  // Save theme and apply to DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)

    if (currentUser?.id) {
      const userThemeKey = `user_${currentUser.id}_theme`
      localStorage.setItem(userThemeKey, theme)
    } else {
      // Save globally when not logged in
      localStorage.setItem('theme', theme)
    }
  }, [theme, currentUser?.id])

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark')
  }

  const refreshUser = async () => {
    try {
      const authStatus = await AuthService.checkAuth()
      if (authStatus.isAuthenticated) {
        setCurrentUser(authStatus.user)
        return true
      } else {
        setCurrentUser(null)
        return false
      }
    } catch (error) {
      console.error('Failed to refresh user:', error)
      setCurrentUser(null)
      return false
    }
  }

  const logout = async () => {
    try {
      const success = await AuthService.logout()
      if (success) {
        AuthService.redirectToLogin()
      }
    } catch (error) {
      console.error('Logout failed:', error)
      AuthService.redirectToLogin()
    }
  }

  const isAdmin = currentUser?.role === 'admin'

  const value = {
    currentUser,
    authChecked,
    isAdmin,
    logout,
    setCurrentUser,
    refreshUser,
    theme,
    toggleTheme
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}